import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { validarRut, validarEmail, formatearRut } from '../utils/validaciones';
import './Dashboard.css';

const Usuarios = () => {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('usuarios');

  const [profanityList, setProfanityList] = useState('');
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);


  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ nombre: '', apellido: '', email: '', rut: '', rol: 'admin' });
  const [formErrors, setFormErrors] = useState({});

  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const apiBase = isLocal ? `http://${window.location.hostname}:3001` : `https://${window.location.hostname}`;

  const fetchUsuarios = async () => {
    setIsLoading(true);
    try {
      const resp = await fetch(`${apiBase}/api/usuarios`);
      if (resp.ok) setUsuarios(await resp.json());
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProfanityList = async () => {
    try {
      const resp = await fetch(`${apiBase}/api/config/profanity`);
      if (resp.ok) {
        const data = await resp.json();
        setProfanityList(data.words);
      }
    } catch (err) {
      console.error('Error fetching profanity list:', err);
    }
  };

  const handleSaveConfig = async () => {
    setIsSavingConfig(true);
    setSaveStatus(null);
    const apiHost = window.location.hostname || 'localhost';
    const isLocal = apiHost === 'localhost' || apiHost === '127.0.0.1';
    const apiUrl = isLocal ? `http://${apiHost}:3001/api/config/profanity` : `https://${apiHost}/api/config/profanity`;

    try {
      const resp = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ words: profanityList })
      });
      if (resp.ok) {
        setSaveStatus('success');
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        const errorData = await resp.json().catch(() => ({}));
        setSaveStatus('error');
        alert('Error del servidor: ' + (errorData.error || resp.statusText));
      }
    } catch (err) {
      console.error('Error saving config:', err);
      setSaveStatus('error');
      alert('Error de conexión: ' + err.message);
    } finally {
      setIsSavingConfig(false);
    }
  };

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (!savedUser) return navigate('/');
    const u = JSON.parse(savedUser);
    if (u.rol !== 'admin') {
      alert('Acceso denegado. Solo los administradores pueden gestionar usuarios.');
      return navigate('/dashboard');
    }
    fetchUsuarios();
    fetchProfanityList();
  }, [navigate]);

  const handleSave = async () => {
    const errors = {};
    if (!formData.nombre) errors.nombre = 'El nombre es requerido.';
    if (!formData.apellido) errors.apellido = 'El apellido es requerido.';
    if (!validarEmail(formData.email)) errors.email = 'Ingresa un email válido (ej: usuario@correo.cl).';
    if (!validarRut(formData.rut)) errors.rut = 'RUT inválido. Usa el formato 12345678-9 y verifica el dígito verificador.';
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});
    const nombreCompleto = `${formData.nombre.trim()} ${formData.apellido.trim()}`;
    try {
      const resp = await fetch(`${apiBase}/api/usuarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre: nombreCompleto, email: formData.email, rut: formData.rut, rol: formData.rol })
      });
      if (resp.ok) {
        setIsModalOpen(false);
        setFormData({ nombre: '', apellido: '', email: '', rut: '', rol: 'admin' });
        fetchUsuarios();
      } else {
        const errorData = await resp.json();
        alert(errorData.error);
      }
    } catch (err) {
      console.error(err);
      alert('Error guardando usuario de sistema.');
    }
  };

  const handleDelete = async (id, nombre) => {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (id === currentUser.id || id === currentUser.uid) {
      alert('Por seguridad, no puedes eliminar tu propia cuenta mientras estás logueado.');
      return;
    }
    if (!window.confirm(`¿Seguro que deseas revocar el acceso y eliminar definitivamente a: ${nombre}?`)) return;
    try {
      const resp = await fetch(`${apiBase}/api/usuarios/${id}`, { method: 'DELETE' });
      if (resp.ok) {
        fetchUsuarios();
      } else {
        alert('No se pudo borrar el usuario.');
      }
    } catch (err) {
      console.error(err);
      alert('Error contactando al servidor.');
    }
  };

  return (
    <div className="dashboard-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>Gestor de Equipos</h2>
        </div>
        <nav className="sidebar-nav">
          <Link to="/dashboard" className="nav-item">Equipos</Link>
          <Link to="/funcionarios" className="nav-item">Funcionarios</Link>
          {(JSON.parse(localStorage.getItem('user') || '{}').rol === 'admin' ||
            JSON.parse(localStorage.getItem('user') || '{}').rol === 'superadmin') && (
              <Link to="/supervisor" className="nav-item">Panel de Análisis</Link>
            )}
          <Link to="/usuarios" className="nav-item active">Usuarios (Admin)</Link>
        </nav>
      </aside>

      <main className="main-content">
        <header className="content-header">
          <h1>Administración del Sistema</h1>
          <Link to="/perfil" className="user-profile" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>{JSON.parse(localStorage.getItem('user') || '{}').nombre || 'Mi Perfil'}</span>
            <div className="avatar" style={{ background: '#0d6efd' }}>{JSON.parse(localStorage.getItem('user') || '{}').nombre?.charAt(0).toUpperCase() || 'P'}</div>
          </Link>
        </header>

        <div className="tabs-container" style={{ display: 'flex', gap: '20px', marginTop: '30px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
          <button
            className={`tab-btn ${activeTab === 'usuarios' ? 'active' : ''}`}
            onClick={() => setActiveTab('usuarios')}
            style={{ padding: '8px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold', color: activeTab === 'usuarios' ? '#0d6efd' : '#64748b', borderBottom: activeTab === 'usuarios' ? '3px solid #0d6efd' : 'none' }}
          > Usuarios </button>
          <button
            className={`tab-btn ${activeTab === 'config' ? 'active' : ''}`}
            onClick={() => setActiveTab('config')}
            style={{ padding: '8px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold', color: activeTab === 'config' ? '#0d6efd' : '#64748b', borderBottom: activeTab === 'config' ? '3px solid #0d6efd' : 'none' }}
          > Configuración de Seguridad </button>
        </div>

        {activeTab === 'usuarios' ? (
          <section className="datatable-section" style={{ marginTop: '20px' }}>
            <div className="datatable-header">
              <h2>Cuentas con Acceso al Gestor</h2>
              <div className="header-actions">
                <button className="btn-primary" onClick={() => setIsModalOpen(true)}>+ Añadir Usuario</button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>RUT Asignado</th>
                    <th>Nombre del Usuario</th>
                    <th>Credencial (Email)</th>
                    <th>Nivel de Privilegios</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>Cargando usuarios...</td></tr>
                  ) : usuarios.length > 0 ? (
                    usuarios.map((u, i) => (
                      <tr key={u.id || i}>
                        <td>{u.rut || 'No Especificado'}</td>
                        <td><strong>{u.nombre}</strong></td>
                        <td style={{ color: '#666' }}>{u.email}</td>
                        <td>
                          <span className="badge" style={{
                            background: u.rol === 'superadmin' ? '#dc3545' :
                              u.rol === 'supervisor' ? '#4f46e5' : '#64748b',
                            color: 'white'
                          }}>
                            {u.rol.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <button className="btn-danger btn-primary" onClick={() => handleDelete(u.id, u.nombre)}>
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="no-results">No hay usuarios extra creados en el sistema.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        ) : (
          <section className="datatable-section" style={{ marginTop: '20px', maxWidth: '800px' }}>
            <div className="datatable-header">
              <h2>Filtro de Integridad y Lenguaje</h2>
            </div>
            <div className="card-body" style={{ background: 'white', padding: '25px', borderRadius: '15px', border: '1px solid #e2e8f0', marginTop: '15px' }}>
              <h3 style={{ fontSize: '16px', marginBottom: '15px', color: '#1e293b' }}>Palabras y Modismos Prohibidos</h3>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px', lineHeight: '1.6' }}>
                Ingresa las palabras, modismos chilenos o términos ofensivos que deseas bloquear en el sistema.
                Los campos de texto (Equipos, Funcionarios, etc.) serán validados automáticamente.
                <strong> Separa cada palabra por una coma (,).</strong>
              </p>

              <div className="input-group full-width">
                <textarea
                  value={profanityList}
                  onChange={(e) => setProfanityList(e.target.value)}
                  placeholder="Ej: weon, culiao, ctm..."
                  style={{ width: '100%', minHeight: '150px', padding: '15px', borderRadius: '10px', border: '2px solid #e2e8f0', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }}
                />
              </div>

              <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                <button
                  className="btn-primary"
                  onClick={handleSaveConfig}
                  disabled={isSavingConfig}
                  style={{ background: isSavingConfig ? '#94a3b8' : '#0d6efd' }}
                >
                  {isSavingConfig ? 'Guardando...' : 'Guardar Configuración'}
                </button>
                {saveStatus === 'success' && (
                  <span style={{ color: '#10b981', fontWeight: 'bold', fontSize: '14px' }}>Guardado correctamente</span>
                )}
                {saveStatus === 'error' && (
                  <span style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '14px' }}>Error al guardar. Intenta de nuevo.</span>
                )}
              </div>

              <div style={{ marginTop: '30px', padding: '15px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ fontSize: '13px', color: '#475569', marginBottom: '10px' }}>Consejo de Seguridad</h4>
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                  Este filtro busca palabras completas. Evita usar palabras muy cortas (como "po") para no generar bloqueos accidentales en palabras legítimas.
                </p>
              </div>
            </div>
          </section>
        )}
      </main>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Añadir Cuenta de Usuario</h2>
              <button className="close-btn" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="crud-form">
              <div className="form-row">
                <div className="input-group">
                  <label>Nombre(s) *</label>
                  <input type="text" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} placeholder="Ej: Marcela" style={formErrors.nombre ? { borderColor: '#dc3545' } : {}} />
                  {formErrors.nombre && <small style={{ color: '#dc3545' }}>{formErrors.nombre}</small>}
                </div>
                <div className="input-group">
                  <label>Apellido(s) *</label>
                  <input type="text" value={formData.apellido} onChange={(e) => setFormData({ ...formData, apellido: e.target.value })} placeholder="Ej: López" style={formErrors.apellido ? { borderColor: '#dc3545' } : {}} />
                  {formErrors.apellido && <small style={{ color: '#dc3545' }}>{formErrors.apellido}</small>}
                </div>
              </div>
              <div className="form-row">
                <div className="input-group full-width">
                  <label>Email (Usuario de acceso) *</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="marcela@municipalidad.cl" style={formErrors.email ? { borderColor: '#dc3545' } : {}} />
                  {formErrors.email && <small style={{ color: '#dc3545' }}>{formErrors.email}</small>}
                </div>
              </div>
              <div className="form-row">
                <div className="input-group full-width">
                  <label>RUT del Funcionario *</label>
                  <input
                    type="text"
                    value={formData.rut}
                    onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                    onBlur={(e) => { if (e.target.value) setFormData(f => ({ ...f, rut: formatearRut(e.target.value) })); }}
                    placeholder="Ej: 12.345.678-9"
                    style={formErrors.rut ? { borderColor: '#dc3545' } : {}}
                  />
                  {formErrors.rut
                    ? <small style={{ color: '#dc3545' }}>{formErrors.rut}</small>
                    : <small style={{ color: '#6c757d', marginTop: '4px', display: 'block' }}>Contraseña inicial: Primeros 8 números del RUT (sin puntos ni DV). Se notificará por correo.</small>
                  }
                </div>
              </div>
              <div className="form-row">
                <div className="input-group full-width">
                  <label style={{ fontWeight: 'bold', fontSize: '13px', color: '#555' }}>Alcance de Privilegios en el Sistema</label>
                  <select value={formData.rol} onChange={(e) => setFormData({ ...formData, rol: e.target.value })} style={{ padding: '11px', borderRadius: '5px', border: '1px solid #ccc', marginTop: '6px', background: 'white' }}>
                    <option value="admin">Administrador (Acceso Total a Ajustes y Usuarios)</option>
                    <option value="supervisor">Supervisor (Panel de Análisis y Estadísticas)</option>
                    <option value="operador">Operador Técnico (Manejo de Equipos y Funcionarios Básicos)</option>
                  </select>
                </div>
              </div>
              <div className="form-footer">
                <button type="button" className="btn-secondary" onClick={() => { setIsModalOpen(false); setFormErrors({}); }}>Cancelar</button>
                <button type="submit" className="btn-primary">Autorizar Usuario</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios;
